import React, { useState, useEffect } from "react";
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  TextareaAutosize
} from '@material-ui/core';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  CloudUpload as UploadIcon
} from '@material-ui/icons';
import RagService from '../services/RagService';
import notify from "../utils/notifier";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  card: {
    marginBottom: theme.spacing(2)
  },
  statusChip: {
    marginLeft: theme.spacing(1)
  },
  actionButton: {
    margin: theme.spacing(1)
  },
  searchResults: {
    maxHeight: 300,
    overflow: 'auto'
  },
  documentUpload: {
    padding: theme.spacing(2),
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center',
    marginTop: theme.spacing(2)
  },
  metricsBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1)
  }
}));

function RagDashboard() {
  const classes = useStyles();
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  // Search functionality removed - backend endpoint not implemented yet
  const [documentContent, setDocumentContent] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  useEffect(() => {
    loadSystemStatus();
  }, []);

  const loadSystemStatus = async () => {
    setLoading(true);
    try {
      const [health, status] = await Promise.all([
        RagService.getRagHealth(),
        RagService.getRagStatus()
      ]);
      setSystemStatus({ health, ...status });
    } catch (error) {
      console.error('Failed to load system status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Search functionality removed until backend endpoint is implemented

  const handleRecovery = async () => {
    setLoading(true);
    try {
      const result = await RagService.recoverFromPvc();
      notify.success("System recovery completed successfully");
      await loadSystemStatus(); // Refresh status
    } catch (error) {
      console.error('Recovery failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleDocumentUpload = async () => {
    if (!documentContent.trim() && !selectedFile) return;
    
    setUploadLoading(true);
    try {
      let result;
      
      if (selectedFile) {
        // Upload file directly
        result = await RagService.addDocumentFile(selectedFile);
        setSelectedFile(null);
        notify.success(`File "${selectedFile.name}" added successfully! ${result.chunks_added} chunks indexed. Total: ${result.total_documents} documents.`);
      } else {
        // Upload text content  
        result = await RagService.addDocument(documentContent);
        setDocumentContent('');
        notify.success(`Document added successfully! ${result.chunks_added} chunks indexed. Total: ${result.total_documents} documents.`);
      }
      
      await loadSystemStatus(); // Refresh status
    } catch (error) {
      console.error('Document upload failed:', error);
    } finally {
      setUploadLoading(false);
    }
  };

  const getStatusColor = (isReady) => isReady ? 'primary' : 'secondary';
  const getStatusIcon = (isReady) => isReady ? <CheckIcon /> : <ErrorIcon />;

  return (
    <div className={classes.root}>
      <Typography variant="h4" gutterBottom>
        RAG System Dashboard
      </Typography>

      {/* System Status */}
      <Card className={classes.card}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">System Status</Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadSystemStatus}
              disabled={loading}
              size="small"
            >
              Refresh
            </Button>
          </Box>
          
          {loading ? (
            <Box display="flex" alignItems="center" mt={2}>
              <CircularProgress size={24} />
              <Typography style={{ marginLeft: 8 }}>Loading status...</Typography>
            </Box>
          ) : systemStatus ? (
            <Grid container spacing={2} style={{ marginTop: 8 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box className={classes.metricsBox}>
                  <Typography variant="body2">System</Typography>
                  <Chip 
                    label={systemStatus.is_ready ? "Ready" : "Not Ready"}
                    color={getStatusColor(systemStatus.is_ready)}
                    icon={getStatusIcon(systemStatus.is_ready)}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box className={classes.metricsBox}>
                  <Typography variant="body2">Documents</Typography>
                  <Typography variant="h6">{systemStatus.documents_count}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box className={classes.metricsBox}>
                  <Typography variant="body2">DB Size</Typography>
                  <Typography variant="h6">{systemStatus.db_size_mb} MB</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box className={classes.metricsBox}>
                  <Typography variant="body2">Last Updated</Typography>
                  <Typography variant="body2">{systemStatus.last_updated || 'Never'}</Typography>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Typography color="error">Failed to load system status</Typography>
          )}
        </CardContent>
      </Card>

      {/* Document Search - Removed until backend search endpoint is implemented */}

      {/* System Actions */}
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" gutterBottom>System Management</Typography>
          <Box>
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleRecovery}
              disabled={loading}
              variant="outlined"
              className={classes.actionButton}
            >
              Rebuild from PVC Documents
            </Button>
            <Typography variant="body2" color="textSecondary">
              Reprocess all training documents from persistent storage
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Document Upload */}
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Add New Document</Typography>
          
          {/* File Upload Option */}
          <Box mb={2}>
            <Typography variant="body2" gutterBottom>Upload File:</Typography>
            <input
              type="file"
              accept=".md,.txt,.json"
              onChange={handleFileSelect}
              style={{ marginBottom: '16px' }}
            />
            {selectedFile && (
              <Typography variant="body2" color="textSecondary">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </Typography>
            )}
          </Box>
          
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Or paste content directly:
          </Typography>
          
          {/* Text Content Option */}
          <TextField
            label="Document Content"
            multiline
            rows={6}
            value={documentContent}
            onChange={(e) => setDocumentContent(e.target.value)}
            variant="outlined"
            fullWidth
            placeholder="Paste your document content here (Markdown format supported)..."
          />
          
          <Box mt={2}>
            <Button
              startIcon={uploadLoading ? <CircularProgress size={16} /> : <UploadIcon />}
              onClick={handleDocumentUpload}
              disabled={uploadLoading || (!documentContent.trim() && !selectedFile)}
              variant="contained"
              color="primary"
            >
              {uploadLoading ? 'Processing...' : selectedFile ? `Upload ${selectedFile.name}` : 'Add Document'}
            </Button>
            {(documentContent.trim() || selectedFile) && (
              <Button
                onClick={() => {
                  setDocumentContent('');
                  setSelectedFile(null);
                }}
                style={{ marginLeft: 8 }}
              >
                Clear
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </div>
  );
}

export default RagDashboard;